import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, User, Mail, Phone, Building, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Calendar component
function BookingCalendar({ selectedDate, onSelectDate, availableDates }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const days = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const calendarDays = [];
    
    // Empty cells for days before the first day of month
    for (let i = 0; i < startingDay; i++) {
      calendarDays.push(null);
    }
    
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      calendarDays.push({
        day: i,
        date: dateStr,
        available: availableDates.includes(dateStr)
      });
    }
    
    return calendarDays;
  };
  
  const calendarDays = getDaysInMonth(currentMonth);
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  const dayElements = [];
  for (let i = 0; i < days.length; i++) {
    dayElements.push(
      <div key={`header-${i}`} className="text-center text-white/60 text-sm font-medium py-2">
        {days[i]}
      </div>
    );
  }
  
  const dateElements = [];
  for (let i = 0; i < calendarDays.length; i++) {
    const dayData = calendarDays[i];
    if (dayData === null) {
      dateElements.push(<div key={`empty-${i}`} />);
    } else {
      const isSelected = selectedDate === dayData.date;
      const isAvailable = dayData.available;
      
      dateElements.push(
        <button
          key={dayData.date}
          onClick={() => isAvailable && onSelectDate(dayData.date)}
          disabled={!isAvailable}
          className={`
            p-2 rounded-lg text-sm font-medium transition-all
            ${isSelected 
              ? 'bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white' 
              : isAvailable 
                ? 'hover:bg-white/10 text-white cursor-pointer' 
                : 'text-white/20 cursor-not-allowed'
            }
          `}
        >
          {dayData.day}
        </button>
      );
    }
  }
  
  return (
    <div className="bg-white/5 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded-lg transition-all">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h3 className="text-white font-semibold">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded-lg transition-all">
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {dayElements}
        {dateElements}
      </div>
      
      <p className="text-center text-white/40 text-xs mt-4">
        Solo Lunes a Viernes disponibles
      </p>
    </div>
  );
}

// Time slots component
function TimeSlots({ slots, selectedTime, onSelectTime, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#f47c2a] animate-spin" />
      </div>
    );
  }
  
  if (!slots || slots.length === 0) {
    return (
      <p className="text-center text-white/60 py-4">
        Selecciona una fecha para ver horarios
      </p>
    );
  }
  
  const slotElements = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const isSelected = selectedTime === slot.time;
    const hour = parseInt(slot.time.split(':')[0]);
    const displayTime = hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
    
    slotElements.push(
      <button
        key={slot.time}
        onClick={() => slot.available && onSelectTime(slot.time)}
        disabled={!slot.available}
        className={`
          p-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2
          ${isSelected 
            ? 'bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white' 
            : slot.available 
              ? 'bg-white/5 hover:bg-white/10 text-white border border-white/10' 
              : 'bg-white/5 text-white/30 cursor-not-allowed line-through'
          }
        `}
      >
        <Clock className="w-4 h-4" />
        {displayTime}
      </button>
    );
  }
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {slotElements}
    </div>
  );
}

// Main Booking Modal
export function BookingModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });
  
  // Load available dates
  useEffect(() => {
    if (isOpen) {
      fetch(`${API_URL}/api/booking/available-dates`)
        .then(res => res.json())
        .then(data => setAvailableDates(data.dates || []))
        .catch(err => console.error('Error loading dates:', err));
    }
  }, [isOpen]);
  
  // Load slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      setLoadingSlots(true);
      setSelectedTime(null);
      fetch(`${API_URL}/api/booking/slots/${selectedDate}`)
        .then(res => res.json())
        .then(data => {
          setSlots(data.slots || []);
          setLoadingSlots(false);
        })
        .catch(err => {
          console.error('Error loading slots:', err);
          setLoadingSlots(false);
        });
    }
  }, [selectedDate]);
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          date: selectedDate,
          time: selectedTime
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al agendar');
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetModal = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({ name: '', email: '', phone: '', company: '', notes: '' });
    setSuccess(false);
    setError(null);
  };
  
  const handleClose = () => {
    resetModal();
    onClose();
  };
  
  if (!isOpen) return null;
  
  // Format selected date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('es-MX', options);
  };
  
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const hour = parseInt(timeStr.split(':')[0]);
    return hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-gradient-to-br from-[#0f0f15] to-[#1a1a25] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-br from-[#0f0f15] to-[#1a1a25] p-6 border-b border-white/10 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {success ? 'Sesión Confirmada' : 'Agendar Sesión Estratégica'}
              </h2>
              {!success && (
                <p className="text-white/60 text-sm mt-1">
                  {step === 1 ? 'Selecciona fecha y hora' : 'Completa tus datos'}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-full transition-all"
            >
              <X className="w-6 h-6 text-white/60" />
            </button>
          </div>
          
          <div className="p-6">
            {success ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                  <Check className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Listo!</h3>
                <p className="text-white/60 mb-6">
                  Tu sesión ha sido agendada para el<br />
                  <span className="text-white font-semibold">{formatDate(selectedDate)}</span><br />
                  a las <span className="text-[#f47c2a] font-semibold">{formatTime(selectedTime)} (CDMX)</span>
                </p>
                <p className="text-white/40 text-sm mb-8">
                  Te enviamos un correo de confirmacion con todos los detalles.
                </p>
                <button
                  onClick={handleClose}
                  className="px-8 py-3 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#e03c3c]/30 transition-all"
                >
                  Cerrar
                </button>
              </motion.div>
            ) : (
              <>
                {/* Step indicators */}
                <div className="flex items-center justify-center gap-4 mb-8">
                  <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#f47c2a]' : 'text-white/40'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#f47c2a]' : 'bg-white/10'}`}>
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium hidden sm:inline">Fecha y Hora</span>
                  </div>
                  <div className="w-12 h-px bg-white/20" />
                  <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#f47c2a]' : 'text-white/40'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#f47c2a]' : 'bg-white/10'}`}>
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium hidden sm:inline">Tus Datos</span>
                  </div>
                </div>
                
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <BookingCalendar
                      selectedDate={selectedDate}
                      onSelectDate={setSelectedDate}
                      availableDates={availableDates}
                    />
                    
                    {selectedDate && (
                      <div>
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#f47c2a]" />
                          Horarios disponibles - {formatDate(selectedDate)}
                        </h4>
                        <TimeSlots
                          slots={slots}
                          selectedTime={selectedTime}
                          onSelectTime={setSelectedTime}
                          loading={loadingSlots}
                        />
                      </div>
                    )}
                    
                    <div className="flex justify-end pt-4">
                      <button
                        onClick={() => setStep(2)}
                        disabled={!selectedDate || !selectedTime}
                        className="px-8 py-3 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#e03c3c]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continuar
                      </button>
                    </div>
                  </motion.div>
                )}
                
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    {/* Summary */}
                    <div className="bg-white/5 rounded-xl p-4 flex items-center gap-4">
                      <div className="p-3 rounded-lg bg-[#f47c2a]/20">
                        <Calendar className="w-5 h-5 text-[#f47c2a]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{formatDate(selectedDate)}</p>
                        <p className="text-[#f47c2a] text-sm">{formatTime(selectedTime)} (CDMX)</p>
                      </div>
                      <button
                        onClick={() => setStep(1)}
                        className="ml-auto text-white/60 hover:text-white text-sm"
                      >
                        Cambiar
                      </button>
                    </div>
                    
                    {error && (
                      <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-300 text-sm">
                        {error}
                      </div>
                    )}
                    
                    {/* Form */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          <User className="w-4 h-4 inline mr-2" />
                          Nombre completo *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={e => setFormData({...formData, name: e.target.value})}
                          required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#f47c2a] focus:ring-1 focus:ring-[#f47c2a] transition-all"
                          placeholder="Tu nombre"
                        />
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white/80 text-sm font-medium mb-2">
                            <Mail className="w-4 h-4 inline mr-2" />
                            Email *
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={e => setFormData({...formData, email: e.target.value})}
                            required
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#f47c2a] focus:ring-1 focus:ring-[#f47c2a] transition-all"
                            placeholder="tu@email.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-white/80 text-sm font-medium mb-2">
                            <Phone className="w-4 h-4 inline mr-2" />
                            WhatsApp *
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={e => setFormData({...formData, phone: e.target.value})}
                            required
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#f47c2a] focus:ring-1 focus:ring-[#f47c2a] transition-all"
                            placeholder="+52 777 123 4567"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          <Building className="w-4 h-4 inline mr-2" />
                          Empresa (opcional)
                        </label>
                        <input
                          type="text"
                          value={formData.company}
                          onChange={e => setFormData({...formData, company: e.target.value})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#f47c2a] focus:ring-1 focus:ring-[#f47c2a] transition-all"
                          placeholder="Nombre de tu empresa"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Cuéntanos brevemente sobre tu proyecto (opcional)
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={e => setFormData({...formData, notes: e.target.value})}
                          rows={3}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#f47c2a] focus:ring-1 focus:ring-[#f47c2a] transition-all resize-none"
                          placeholder="Que te gustaria lograr con tu marca?"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => setStep(1)}
                        className="px-6 py-3 bg-white/10 text-white font-medium rounded-full hover:bg-white/20 transition-all"
                      >
                        Atras
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={submitting || !formData.name || !formData.email || !formData.phone}
                        className="flex-1 px-8 py-3 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#e03c3c]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Agendando...
                          </>
                        ) : (
                          'Confirmar Sesión'
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default BookingModal;
